/**
 * Category dropdown options for the Update Feed filter (Story 5.3 / SE-4863).
 *
 * The dropdown surfaces a literal "All" sentinel followed by one option per
 * `FeedEventCategory` enum value. Labels mirror the ANNEX EPIC §6 copy and
 * are sourced here so the dropdown View, archive-confirmation copy
 * (SE-4862), and any future filter-aware surface stay in sync via a single
 * declaration.
 * @module features/update-feed/utils/categoryFilterOptions
 */

import { FeedEventCategory } from "@/generated/graphql";

/** Sentinel for the "no filter applied" dropdown selection. */
export const ALL_CATEGORIES_VALUE = "ALL" as const;

/**
 * Discriminated value for the dropdown — either the literal "ALL" sentinel
 * or one of the FeedEventCategory enum values. Keeping the sentinel as a
 * string (rather than `null`) lets the underlying GlueStack `Select`
 * (`selectedValue: string`) drive the control directly.
 */
export type CategoryFilterValue =
  | typeof ALL_CATEGORIES_VALUE
  | FeedEventCategory;

/** Single dropdown option (value + human-readable label). */
export interface CategoryFilterOption {
  /** Underlying value forwarded to GlueStack `Select` and to `onChange`. */
  readonly value: CategoryFilterValue;
  /** Visible label (e.g. "Expiring contracts"). */
  readonly label: string;
}

/**
 * Ordered options for the dropdown — "All" first, then the six categories
 * in ANNEX EPIC §6 order (Alerts → Expiring contracts → Recent transfers →
 * Recommended players → Scouting → Activity).
 */
export const CATEGORY_FILTER_OPTIONS: readonly CategoryFilterOption[] = [
  { value: ALL_CATEGORIES_VALUE, label: "All" },
  { value: FeedEventCategory.Alerts, label: "Alerts" },
  { value: FeedEventCategory.ExpiringContracts, label: "Expiring contracts" },
  { value: FeedEventCategory.RecentTransfers, label: "Recent transfers" },
  { value: FeedEventCategory.RecommendedPlayers, label: "Recommended players" },
  { value: FeedEventCategory.Scouting, label: "Scouting" },
  { value: FeedEventCategory.Activity, label: "Activity" },
];

/**
 * Map a dropdown value to the `categories` GraphQL variable. The "ALL"
 * sentinel maps to `undefined` (omit the filter); a category value maps to
 * a single-element array.
 * @param value - Current dropdown selection.
 * @returns Argument to forward as `categories`, or `undefined` to omit.
 */
export function toCategoriesVariable(
  value: CategoryFilterValue
): FeedEventCategory[] | undefined {
  return value === ALL_CATEGORIES_VALUE ? undefined : [value];
}

/**
 * Map a dropdown value to its human label (used by the archive-confirmation
 * copy in SE-4862). "ALL" returns the empty string so callers can render
 * `Archive all updates` vs. `Archive all Expiring contracts updates`.
 * @param value - Current dropdown selection.
 * @returns Human label, or empty string when no category is filtered.
 */
export function categoryFilterLabel(value: CategoryFilterValue): string {
  if (value === ALL_CATEGORIES_VALUE) return "";
  const opt = CATEGORY_FILTER_OPTIONS.find(o => o.value === value);
  return opt?.label ?? "";
}
